import { action, computed, observable } from 'mobx';
import Alert from 'react-s-alert';
import { analytics } from 'core/analytics';

import { OptionsQuery } from 'apps/rfq/gql';

import { AppManager, Variables } from 'core';
import { FormStore } from 'libs/forms/app';
import { clientStore } from 'apps/client/app';
import type { FieldStore } from 'libs/forms/fieldStores';

import {
  BuildApp,
  JobSpecApp,
  OptionModel,
  OptionValueModel,
  valueKey,
} from 'apps/rfq/app';

const buildModelConfig = {
  buildModelForm: {
    fields: ['process', 'material'], // , 'finishing', 'secondaryFinishing'],
    styles: {
      formColumns: 2,
      labelBold: true,
    },
  },
  title: 'Part Details.',
  fields: {
    process: {
      type: 'process',
    },
    material: {
      type: 'material',
    },
  },
};
// jobSpecConfig
const jobSpecConfig = {
  jobSpecForm: {
    fields: ['quantity'],
    styles: {
      formColumns: 1,
      labelBold: true,
    },
  },
  quoteJobSpecForm: {
    fields: ['leadTime'],
    styles: {
      formColumns: 2,
      labelBold: true,
    },
  },
  title: 'Order Details.',
  fields: {
    quantity: {
      type: 'jobSpecQuantity',
    },
    // When quoting we allow the user to enter a custom manufacturing time
    leadTime: {
      label: 'Manufacturing Time (business days)',
      placeholder: 'e.g. 10',
      component: 'forms.TextField',
      fieldStore: 'forms.IntegerFieldStore',
      minVal: 1,
      maxVal: 1000,
    },
  },
};

function createOptionSelectField(option: OptionModel) {
  const { values, required, nilLabel, valueType } = option;
  let { name } = option;
  // set undefined to null and create default values on the target?
  let defaultVal;
  if (nilLabel && values.length !== 0) {
    const defaultValueData = {
      id: `autoGeneratedDefaultFor-${option.id}`,
      default: true,
      option: option.toJs(null, true),
      valueString: nilLabel,
    };
    const defaultValue = OptionValueModel.Load(defaultValueData);

    defaultVal = defaultValue;
    values.unshift(defaultValue);
  }
  const explicitdefaultVal = values.find(val => val.default);
  if (explicitdefaultVal !== undefined) {
    defaultVal = explicitdefaultVal;
  }
  let component = 'forms.SelectField';
  const fieldStore = 'forms.ReferenceFieldStore';
  if (valueType === 'BOOL') {
    component = 'forms.CheckboxField';
  }

  // Hack to deal with the fact the server still returns a 'Lead Time' Option/OptionValue
  if (name === 'Lead Time') {
    name = 'Manufacturing Time';
  }
  // Set server provided default values here in the future
  const fieldConf = {
    label: `${name}`,
    placeholder: nilLabel || `Choose ${name}`,
    component,
    fieldStore,
    optional: !required,
    boldLabel: true,
    noValidation: true,
    labelKey: 'text',
    appKey: 'id',
    highlightNextDisabled: name === 'Manufacturing Time',
    // selectionComponent: 'rfq.optionSelection',
    options: {
      allowNew: false,
      // optionComponent: 'rfq.optionOption',
      values,
      keepOpenOnSelect: false,
    },
  };
  if (defaultVal) {
    fieldConf.options.default = defaultVal;
  }
  if (valueType === 'BOOL') {
    fieldConf.valueKey = 'value';
    fieldConf.descriptionChecked = `Has ${name}`;
    fieldConf.descriptionUnChecked = `No ${name}`;
  }
  return fieldConf;
}

// eslint-disable-next-line
function createOptionMinMaxField(option: OptionModel) {
  // write me
}

const formStoreToFieldStores = (optionsFormStore: FormStore) => {
  if (!optionsFormStore) {
    return [];
  }
  return (
    optionsFormStore.fieldConf
      .map((conf: any) => optionsFormStore.fieldStores.get(conf.fieldName))
      .filter((fieldStore: FieldStore) => !!fieldStore)
      // filter out options with no option instances
      // which we will display elsewhere
      .filter(
        (fieldStore: FieldStore) => fieldStore.conf.options.values.length > 0,
      )
  );
};

export class JobSpecAppManager extends AppManager {
  static registryName = 'JobSpecAppManager';

  buildFormStore: FormStore;

  jobSpecFormStore: FormStore;

  optionsQ: OptionsQuery;

  @observable isQuote: boolean = false;

  @observable jobSpecApp: JobSpecApp = null; // passed in from the parentAppManager

  @observable processOptionsFormStore: FormStore = null;

  @observable materialOptionsFormStore: FormStore = null;

  constructor(parentAppManager: AppManager, props: any, options: any) {
    super(parentAppManager, props, options);
    if (!this.jobSpecApp?.build) {
      this.jobSpecApp.build = new BuildApp(this.jobSpecApp, {
        // jobSpec: this.jobSpecApp,
      });
    }

    // This appManager takes ownership of this app so that the form (Tree)OptionsManager
    // can see the variables defined here and requeryOn them.
    this.jobSpecApp.setManager(this);

    // variables from this jobSpecAppManager will be implicitly available on
    // the formstore's 'options' query and OptionsManager
    this.variables = new Variables({
      materialFilter: () => {
        return {
          processId: this.jobSpecApp.build.process?.id || null,
          name: this.jobSpecApp.build.process?.processName || null,
        };
      },
      processFilter: () => {
        return {
          materialId: this.jobSpecApp.build.material?.id || null,
          name: this.jobSpecApp.build.material?.materialName || null,
        };
      },
      optionFilter: () => {
        return {
          processId: this.jobSpecApp.build.process?.id || null,
          materialId: this.jobSpecApp.build.material?.id || null,
        };
      },
    });
    this.variables.onChange(['optionFilter'], this.LoadOptions);

    // only instantiate forms when creating new RFQs. Re-evaluate once we
    // allow editing of existing RFQs.
    this.buildFormStore = new FormStore(
      this.jobSpecApp.build,
      buildModelConfig,
      'buildModelForm',
      false, // autoFocusFirstField
    );

    let jobSpecForm = 'jobSpecForm';
    if (this.isQuote) {
      jobSpecForm = 'quoteJobSpecForm';
    }

    this.jobSpecFormStore = new FormStore(
      this.jobSpecApp,
      jobSpecConfig,
      jobSpecForm,
      false, // autoFocusFirstField
    );
  }

  @computed get submitting() {
    return this.buildFormStore.posting || this.jobSpecFormStore.posting;
  }

  @computed get formStores() {
    const stores = [this.buildFormStore, this.jobSpecFormStore];
    if (this.materialOptionsFormStore) {
      stores.push(this.materialOptionsFormStore);
    }
    if (this.processOptionsFormStore) {
      stores.push(this.processOptionsFormStore);
    }
    return stores;
  }

  alert = () => {
    this.formStores.forEach(store => !store.isValid && store.alert());
  };

  @computed get isValid() {
    // eslint-disable-next-line no-restricted-syntax
    for (const store of this.formStores) {
      if (!store.isValid) {
        return false;
      }
    }
    return true;
  }

  @action SetPosting = (boolVal: boolean) => {
    this.formStores.forEach(store => store.SetPosting(boolVal));
  };

  @computed get loadingOptionFields() {
    // options.loading is true also when loading e.g. material options.
    // this computed is used to show a loading spinner, only when we load
    // option fields, hence checking if process / material is set.
    if (
      this.jobSpecApp.build.process?.id &&
      this.jobSpecApp.build.material?.id
    ) {
      return this.queries.get('options')?.loading;
    }
    return false;
  }

  @action OptionUpdate = (result: any) => {
    // why is this sometimes called twice while LoadOptions and Query is called only once
    // (when making changes after initial selection?)
    // Perhaps due to re-selection of valid option? (but should not cause update)

    const options: any = {};
    result.forEach((option: OptionModel) => {
      options[option.id] = createOptionSelectField(option);
    });
    // For now we generate a formStore with all Options for the Material + Process
    // This means that we can have a relatively stable optionsFormStore so long as
    // Material or Process is not changed, the list of Options will not change.
    // What might change is whether specific Options are rendered (or have no optionInstance).
    // And the list of available options

    const optionsFormConf = {
      materialOptionsForm: {
        fields: result
          .filter((opt: OptionModel) => opt.for.__typename === 'Material')
          .map((opt: OptionModel) => opt.id),
      },
      processOptionsForm: {
        fields: result
          .filter((opt: OptionModel) => opt.for.__typename === 'Process')
          .filter(
            (opt: OptionModel) => !(opt.name === 'Lead Time' && this.isQuote),
          )
          .map((opt: OptionModel) => opt.id),
      },
      title: 'Options.', // unused
      fields: options,
    };
    // if we are changing an existing JobSpec, we need to set the valuesTarget
    const valuesTarget: any = {};
    const buildModelVals = this.jobSpecApp?.build?.values;
    if (buildModelVals && Object.keys(options).length > 0) {
      // eslint-disable-next-line no-restricted-syntax
      for (const buildVal of buildModelVals) {
        const optValModel = options[buildVal.of.id]?.options.values.find(
          (optVal: OptionValueModel) =>
            optVal.value === buildVal[valueKey(optVal.option)],
        );

        if (optValModel) {
          valuesTarget[optValModel.option.id] = optValModel;
        }
      }
      this.jobSpecApp.build.valuesTarget = valuesTarget;
    }
    this.materialOptionsFormStore = new FormStore(
      this.jobSpecApp.build.valuesTarget,
      optionsFormConf,
      'materialOptionsForm',
      false, // autoFocusFirstField
    );
    this.processOptionsFormStore = new FormStore(
      this.jobSpecApp.build.valuesTarget,
      optionsFormConf,
      'processOptionsForm',
      false, // autoFocusFirstField
    );
  };

  @computed get materialOptionsFieldStores(): FieldStore[] {
    return formStoreToFieldStores(this.materialOptionsFormStore);
  }

  @computed get processOptionsFieldStores(): FieldStore[] {
    return formStoreToFieldStores(this.processOptionsFormStore);
  }

  @computed get processOptionsBuildFieldStores(): FieldStore[] {
    return this.processOptionsFieldStores.filter(
      (option: FieldStore) => option.conf.label !== 'Manufacturing Time',
    );
  }

  @computed get manufacturingTimeFieldStore(): FieldStore {
    return this.processOptionsFieldStores.find(
      (option: FieldStore) => option.conf.label === 'Manufacturing Time',
    );
  }

  onOptionsQueryError = () => {
    Alert.error('Could not get build options.');
  };

  @action LoadOptions = () => {
    this.optionsQ = new OptionsQuery(this, {
      queryKey: 'options',
      onSysErr: this.onOptionsQueryError,
    });
    this.optionsQ.process((options: OptionModel[] | null) => {
      if (options === null) {
        // eslint-disable-next-line no-param-reassign
        options = [];
      }
      return options.map(opt => OptionModel.Load(opt));
    }, this.OptionUpdate);

    this.optionsQ.query();

    if (this.formLoaded === false) {
      this.formLoaded = true;
    } else {
      analytics.sendEvent(
        'make-part-details-added',
        {},
        {
          ga: true,
          intercom: true,
        },
      );
    }
  };
}
